import {
  Waves, FlaskConical, Brush, Filter, Droplets, Wrench, Sparkles, ClipboardCheck,
} from "lucide-react";

export const SITE = {
  name: "Point of Pines Pools & Service",
  shortName: "Point of Pines Pools",
  domain: "https://poppools.com",
  phone: "(727) 744-5908",
  phoneHref: "tel:+17277445908",
  email: "mike@poppools.com",
  emailHref: "mailto:mike@poppools.com",
  region: "Pinellas County, Florida",
};

export const IMAGES = {
  heroPool: "https://images.unsplash.com/photo-1635111300299-e6e677daf7b7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600&auto=format",
  clearWater: "https://images.unsplash.com/photo-1714203172156-4c2f8c767a37?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200&auto=format",
  technician: "https://images.unsplash.com/photo-1745570295714-cb18cd15ff64?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200&auto=format",
  waterTesting: "/images/test-kit.webp",
  waterTestHand: "/images/water-test-hand.webp",
  aerialHome: "https://images.unsplash.com/photo-1638310081327-5b4b5da6d155?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400&auto=format",
  patio: "https://images.unsplash.com/photo-1657383543368-7d929944be6a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200&auto=format",
  coastal: "https://images.unsplash.com/photo-1599409637219-d04e9a2db432?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400&auto=format",
  ownerMike: "/images/owner-mike.webp",
  ownerSkimming: "/images/mike-skimming.webp",
  mikeNet: "/images/mike-skimming-net.webp",
  mikePole: "/images/mike-pole.webp",
  heroSpa: "/images/hero-spa-pool.webp",
  oceanfrontPool: "/images/oceanfront-pool.webp",
  rentalBeachPool: "/images/rental-beach-pool.webp",
  muralPool: "/images/mural-pool.webp",
  equipmentVisit: "/images/equipment-visit.webp",
  startupPool: "/images/startup-pool.webp",
  servicePool: "/images/service-pool.webp",
  equipmentBefore: "/images/equipment-before.webp",
  equipmentAfter: "/images/equipment-after.webp",
  communityPool: "/images/community-pool.webp",
};

export const CPO = {
  holder: "Michael Strawn",
  credential: "Certified Pool & Spa Operator (CPO)",
  issuer: "Pool & Hot Tub Alliance",
  certNumber: "C-025145",
  certified: "February 2023",
  expires: "February 29, 2028",
  image: "/images/cpo-certificate.jpg",
};

export const CAPABILITIES_PDF = "/files/commercial-pool-capabilities.pdf";
export const RENTAL_PDF = "/files/vacation-rental-pool-service.pdf";
export const LOGO = {
  chip: "/images/logo-chip.png",
  badge: "/images/logo-badge.png",
};

export const NAV_LINKS = [
  { label: "Pool Service", path: "/pool-service/" },
  { label: "Equipment & Repairs", path: "/pool-repairs/" },
  { label: "Pool Startups", path: "/pool-startups/" },
  { label: "Vacation Rentals", path: "/vacation-rental-pool-service/" },
  { label: "Commercial", path: "/commercial-pool-service/" },
  { label: "Service Areas", path: "/service-areas/" },
  { label: "About", path: "/about/" },
  { label: "Contact", path: "/contact/" },
];

export const SERVICES = [
  {
    icon: Waves,
    title: "Weekly Pool Cleaning & Maintenance",
    blurb: "Dependable weekly visits that keep your pool clear, clean, and swim-ready all year long.",
  },
  {
    icon: FlaskConical,
    title: "Water Testing & Chemical Balancing",
    blurb: "Professional-grade testing and precise chemical balancing on every visit — no guesswork.",
  },
  {
    icon: Brush,
    title: "Brushing, Skimming & Vacuuming",
    blurb: "Walls, steps, surface and floor — brushed, skimmed, and vacuumed as needed, every visit.",
  },
  {
    icon: Filter,
    title: "Filter Cleaning & Equipment Care",
    blurb: "Routine filter cleaning and equipment checks that catch small issues before they grow.",
  },
  {
    icon: Droplets,
    title: "Saltwater Pool Maintenance",
    blurb: "Salt cell inspections, chemistry management, and full care for salt chlorine systems.",
  },
  {
    icon: Wrench,
    title: "Equipment Troubleshooting & Repairs",
    blurb: "Pumps, filters, heaters, and more — diagnosed honestly and repaired the right way.",
  },
  {
    icon: Sparkles,
    title: "New Pool / New Surface Startups",
    blurb: "Careful, documented startups for new plaster and resurfaced pools — done by the book.",
  },
  {
    icon: ClipboardCheck,
    title: "Startup Documentation & Reporting",
    blurb: "Detailed water chemistry logs and startup records you and your builder can rely on.",
  },
];

export const SERVICE_OPTIONS = [
  ...SERVICES.map((s) => s.title),
  "Short-Term Rental Pool Service",
  "Commercial Pool Maintenance",
  "Not Sure Yet — Help Me Decide",
];

export const FAQS = [
  {
    q: "What is included in weekly pool service?",
    a: "Every weekly visit typically includes professional water testing and chemical balancing, skimming the surface, brushing walls and steps, vacuuming as needed, emptying skimmer and pump baskets, and a visual check of your equipment. You'll know exactly what was done after each visit.",
  },
  {
    q: "How often does a Florida pool need to be serviced?",
    a: "In Pinellas County's year-round swim season, weekly service is the standard. Our heat, afternoon storms, and heavy use change water chemistry fast — weekly visits keep your water safe, clear, and balanced instead of playing catch-up.",
  },
  {
    q: "Do you service saltwater pools?",
    a: "Yes. Saltwater pools still need regular testing, balancing, brushing, and equipment care — the salt cell itself also needs inspection and periodic cleaning. We maintain both the salt system and the chemistry around it.",
  },
  {
    q: "How often should my pool filter be cleaned?",
    a: "It depends on your filter type, bather load, and surrounding landscaping, but most residential filters in Florida need a thorough cleaning every few months. We monitor your filter during routine visits and let you know before performance drops.",
  },
  {
    q: "What happens to my pool after a big storm?",
    a: "Heavy rain and tropical systems dilute chemicals and blow in debris. After a storm, your pool may need extra attention to rebalance and recover — we'll assess it and communicate clearly about anything beyond your normal visit.",
  },
  {
    q: "Are chemicals included in weekly service?",
    a: "Every pool is different, so your quote will clearly outline exactly what is included for your pool — no vague promises and no surprises. Request a quote and we'll walk you through it.",
  },
  {
    q: "Do you offer pool service for Airbnbs and vacation rentals?",
    a: "Yes — we're a trusted pool care partner of multiple property management companies in Pinellas County. Short-term rental pools can be serviced 1x, 2x, or 3x weekly depending on occupancy, with photo documentation and prompt issue alerts so a pool problem never becomes your next bad review. We treat your rental business like our own.",
  },
  {
    q: "Do you service commercial pools?",
    a: "Yes. We provide commercial pool maintenance for condominiums, HOAs, apartment communities, and hospitality properties across Pinellas County. As a CPO certified company experienced in commercial property pool maintenance, we manage chemistry, documentation, and equipment care to commercial standards.",
  },
  {
    q: "How do I get started with service?",
    a: "Call (727) 744-5908 or request a quote online. We'll learn about your pool and equipment, answer your questions, confirm a service day, and get you on the schedule.",
  },
  {
    q: "Which areas do you serve?",
    a: "All of Pinellas County — Clearwater, Largo, St. Petersburg, Seminole, Dunedin, Palm Harbor, Safety Harbor, Tarpon Springs, Belleair, and the beach communities from Clearwater Beach to St. Pete Beach, plus everywhere in between.",
  },
];

export const WHY_CHAPTERS = [
  {
    num: "01",
    title: "Reliability you can set your watch by",
    text: "Your pool gets serviced on a consistent day, on a consistent route, by people who show up. No wondering if anyone came — dependable weekly pool service is the entire point.",
  },
  {
    num: "02",
    title: "Professional water chemistry, CPO certified",
    text: "We are CPO certified and treat water chemistry as a science, not a splash-and-dash. Professional testing and precise balancing protect your swimmers, your surface, and your equipment.",
  },
  {
    num: "03",
    title: "Clear, detailed communication",
    text: "You always know what was done, what your water needs, and when something needs attention. Straight answers, detailed reporting, and a local pro you can actually reach.",
  },
  {
    num: "04",
    title: "Locally owned in Pinellas County",
    text: "We're not a national franchise call center. We live and work here — from Clearwater to St. Pete to Tarpon Springs — and our reputation is built one backyard at a time.",
  },
];

export const HERO_LINES = ["Professional Pool Service", "Across Pinellas County"];
